import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { SERVICES, IMAGES } from "../../data/site";

export default function Services() {
  const [hover, setHover] = useState(null);
  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-36 bg-white border-t border-black/10"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="w-6 h-px bg-[#FF331F]" />
              <span className="font-mono-ibm text-[11px] tracking-[0.2em] uppercase text-[#FF331F]">
                Services / 02
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] font-medium">
              Six disciplines.
              <br />
              One delivery model.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 lg:pt-4">
            <p className="text-lg text-[#404040] leading-relaxed">
              Every engagement is run by a senior consultant who is accountable
              end-to-end — from discovery and architecture through to production
              hand-off and operations.
            </p>
          </div>
        </div>

        {/* Swiss grid 3x2 */}
        <div className="border border-black/10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 bg-black/10 gap-px">
          {SERVICES.map((s, i) => (
            <motion.div
              key={s.code}
              data-testid={`service-card-${s.code}`}
              onMouseEnter={() => setHover(s.code)}
              onMouseLeave={() => setHover(null)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="bg-white p-8 md:p-10 min-h-[300px] flex flex-col justify-between group cursor-default relative overflow-hidden"
            >
              <div
                className="absolute inset-0 origin-bottom transition-transform duration-500 ease-out -z-0"
                style={{
                  backgroundColor: s.accent,
                  transform:
                    hover === s.code ? "scaleY(1)" : "scaleY(0)",
                }}
              />
              <div className="relative z-10">
                <div className="flex items-start justify-between">
                  <span
                    className="font-mono-ibm text-xs tracking-widest transition-colors"
                    style={{
                      color: hover === s.code ? "#ffffff" : s.accent,
                    }}
                  >
                    [ {s.code} ]
                  </span>
                  <ArrowUpRight
                    size={20}
                    className={`${
                      hover === s.code ? "text-white" : "text-[#0A0A0A]"
                    } transition-all duration-300 ${
                      hover === s.code ? "rotate-0" : "-rotate-45"
                    }`}
                  />
                </div>
                <h3
                  className={`mt-10 font-display text-2xl md:text-3xl leading-tight tracking-tight ${
                    hover === s.code ? "text-white" : "text-[#0A0A0A]"
                  } transition-colors`}
                >
                  {s.title}
                </h3>
                <p
                  className={`mt-4 text-sm md:text-base leading-relaxed ${
                    hover === s.code ? "text-white/90" : "text-[#404040]"
                  } transition-colors`}
                >
                  {s.desc}
                </p>
              </div>
              <ul
                className={`relative z-10 mt-6 space-y-1.5 text-xs font-mono-ibm ${
                  hover === s.code ? "text-white/90" : "text-[#404040]"
                } transition-colors`}
              >
                {s.bullets.map((b) => (
                  <li key={b} className="flex items-center gap-2">
                    <span
                      className="inline-block w-1 h-1"
                      style={{
                        backgroundColor:
                          hover === s.code ? "#ffffff" : s.accent,
                      }}
                    />
                    {b}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Abstract image strip */}
        <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 border border-black/10 overflow-hidden">
            <img
              src={IMAGES.abstract}
              alt="Systems abstract"
              className="w-full h-[280px] object-cover"
            />
          </div>
          <div className="lg:col-span-5">
            <div className="font-mono-ibm text-xs uppercase tracking-widest text-[#FF331F]">
              [ Delivery model ]
            </div>
            <p className="mt-4 font-display text-2xl md:text-3xl tracking-tight leading-tight">
              Fixed scope, fixed price, fixed accountability — when the brief
              is clear. T&amp;M with weekly burn reports — when it isn’t.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
